/**
 * Savoirs Palestre — notions clés expliquées au dirigeant.
 *
 * Chaque notion = un article à sections typées (gabarit constant, contenu
 * variable). C'est le niveau « comprendre » entre le Lexique (1 ligne / terme)
 * et docs/savoirs/ (synthèse académique / bibliographie, hors app).
 *
 * Référentiel GLOBAL (pas de tenant_id) : identique pour tous les clients.
 *
 * Structure d'une notion :
 *   { id, icon, title, category, summary, updated, pages?: [pageKey], sections: [...] }
 *
 * `icon` : le pictogramme de la fiche (clé de components/knowledge/ficheIcons.js).
 *
 * `pages` (2026-09-16) : les écrans que la fiche éclaire. Le volet Savoirs la
 * propose en tête de « Sur cette page » quand l'un d'eux est ouvert.
 *
 * Catégories : 'Juridique' | 'Méthode' | 'Finance'
 *
 * Types de section (`kind`) :
 *   - 'law'          : extrait de loi → { label, articles: [{ ref, text }], source }
 *   - 'text'         : explication    → { label, paragraphs: [string] }
 *   - 'illustration' : exemple chiffré→ { label, paragraphs?, table? {head, rows, note} }
 *   - 'palestre'     : ancrage app    → { label, paragraphs, link? {pageKey, label} }
 *   - 'sources'      : références      → { label, items: [string] }
 *   - 'chart'        : figure vivante  → { label, chart: 'time-bridge' }
 *                      Le TEXTE des fiches reste global ; seule une figure de
 *                      ce type lit les chiffres du client connecté.
 */

const SAVOIRS = [
  {
    id: 'cascade-temps-palestre',
    icon: 'hourglass',
    title: 'Cascade des temps Palestre',
    category: 'Méthode',
    summary:
      'La partition du temps TT → TO → TR : inspirée des taux de rendement ' +
      'industriels, adaptée au TD-ABC. Elle aboutit à deux indicateurs : la ' +
      'capacité normale, dénominateur stable du coût horaire, et l’idle ' +
      'time, déduit plutôt que mesuré.',
    updated: '2026-08-22',
    pages: ['time-standard', 'hr-time-hub', 'chr-pcr', 'cost-rates-hub'],
    sections: [
      {
        kind: 'text',
        label: 'Origine et intention',
        paragraphs: [
          'La cascade des temps de Palestre a deux filiations. L’inspiration ' +
            'vient du modèle industriel des taux de rendement, le TRS et sa ' +
            'décomposition du temps en étages, chaque retranchement nommé ' +
            '(fiche « Taux de rendement en industrie »). L’adaptation vient ' +
            'de la méthode TD-ABC (Kaplan) : ici la cascade ne mesure pas la ' +
            'performance d’une machine, elle établit un coût horaire.',
          'C’est une partition du temps (chaque heure due appartient à une ' +
            'catégorie et une seule), construite pour produire deux ' +
            'indicateurs : la capacité normale, et le temps inutilisé obtenu ' +
            'par déduction.',
        ],
      },
      {
        kind: 'illustration',
        label: 'La cascade',
        table: {
          head: ['Temps', 'Définition', 'Retranché en amont'],
          rows: [
            [
              'TT — Temps total',
              'Temps contractuel sur jours ouvrés : les heures dues par ' +
                'chaque contrat',
              'Week-ends, jours fériés, congés offerts, fermetures ' +
                'd’entreprise',
            ],
            [
              'TO — Temps ouvert',
              'Présence rémunérée due à l’entreprise, avant tout événement ' +
                'et avant toute affectation du temps',
              'Vacances payées',
            ],
            [
              'TR — Capacité pratique',
              'Temps productif normalisé, le dénominateur du coût horaire ' +
                '(CCR)',
              'Congés spéciaux (absences planifiées et indemnisées : ' +
                'maternité, service) et temps de gestion (administration, ' +
                'direction, supervision de production)',
            ],
          ],
          note:
            'En aval, le TR ne se décompose plus : il s’utilise. Les heures ' +
            's’affectent en facturable (TF), non facturable et absentéisme ; ' +
            'le solde est l’idle : déduit, jamais saisi.',
        },
      },
      {
        kind: 'chart',
        label: 'Votre cascade',
        chart: 'time-bridge',
      },
      {
        kind: 'text',
        label: 'Les deux indicateurs de sortie',
        paragraphs: [
          'La capacité normale. La capacité pratique (TR) est le ' +
            'dénominateur du coût horaire (CCR). Dans l’esprit du TD-ABC, ' +
            'elle ne doit pas varier fortement d’une année à l’autre : un ' +
            'dénominateur stable donne des taux comparables dans le temps, ' +
            'et le coût horaire ne se gonfle pas quand l’activité baisse. Le ' +
            'principe rejoint la « capacité normale » des référentiels ' +
            'comptables (IAS 2) : les coûts de structure se répartissent sur ' +
            'une capacité normale, pas sur la production réelle d’une ' +
            'mauvaise année.',
          'L’idle time, par déduction. Le temps inutilisé ne peut pas se ' +
            'mesurer : personne ne saisit ses heures inoccupées. On mesure ' +
            'ce qui s’affecte (facturable, non facturable, absences) et ' +
            'l’idle est le solde : TR moins temps affecté. Multiplié par le ' +
            'CCR, il devient un montant, le coût de la capacité inemployée, ' +
            'visible au lieu d’être dissous dans les taux.',
        ],
      },
      {
        kind: 'illustration',
        label: 'L’usage de la capacité pratique',
        paragraphs: [
          'En aval du TR, la cascade s’arrête : le temps ne se décompose ' +
            'plus, il s’affecte. Chaque heure de capacité pratique part vers ' +
            'une destination, et la liste des destinations est un choix de ' +
            'pilotage, pas une donnée de la méthode.',
        ],
        table: {
          head: ['Affectation', 'Exemple', 'Ce qu’on y lit'],
          rows: [
            [
              'Facturable (TF)',
              'Heures passées sur un mandat et refacturées',
              'Le taux de facturation (TF ÷ TR) et, par différence avec ' +
                'les heures retenues au client, le write-off',
            ],
            [
              'Non facturable (NF)',
              'Avant-vente, offres, reprise d’un travail non refacturé',
              'Le prix commercial de l’activité : ce qu’il faut investir en ' +
                'heures pour décrocher les heures vendues',
            ],
            [
              'Activité interne affectée',
              'Service client mutualisé, ressources humaines, informatique, ' +
                'formation interne, recherche et développement',
              'Le coût d’une fonction qu’aucune facture ne porte, chiffré en ' +
                'heures puis en francs par le coût horaire',
            ],
            [
              'Absentéisme réel',
              'Maladie, imprévu',
              'Une perte d’usage, mesurée au mensuel ; elle reste DANS le ' +
                'TR : imprévisible, elle ne peut pas amputer un dénominateur ' +
                'qui doit rester stable',
            ],
            [
              'Idle — capacité inemployée',
              'Le solde',
              'Ce que personne ne saisit : TR moins tout ce qui précède. ' +
                'Multiplié par le coût horaire, c’est un montant',
            ],
          ],
          note:
            'Une affectation ne se crée pas par confort d’analyse : chaque ' +
            'catégorie ouverte est une saisie de plus pour le collaborateur. ' +
            'La règle est de n’en ouvrir une que si une décision en dépend : ' +
            'un service client mutualisé mérite sa ligne le jour où l’on ' +
            'envisage de le facturer, pas avant.',
        },
      },
      {
        kind: 'illustration',
        label: 'La transposition au service — mêmes sigles, même logique',
        paragraphs: [
          'Palestre reprend volontairement les conventions de la norme (TT, ' +
            'TO, TR) pour garder sa logique de cascade en l’adaptant à une ' +
            'entreprise de services. Le principe de conversion tient en une ' +
            'phrase : le temps total d’une ressource est le temps pendant ' +
            'lequel elle est engagée envers vous. Une machine possédée l’est ' +
            '24 h sur 24 : son temps total est le calendrier. Un ' +
            'collaborateur l’est pour les heures dues à son contrat : son ' +
            'temps total est le contrat. La cascade descend ensuite à ' +
            'l’identique : chaque étage retranche une catégorie ' +
            'd’indisponibilité planifiée, et chaque retranchement est nommé.',
        ],
        table: {
          head: ['Norme (machine)', 'Palestre (collaborateur)', 'Conversion'],
          rows: [
            [
              'tT — temps calendaire',
              'TT — heures dues au contrat',
              'Le contrat joue pour la personne le rôle du calendrier pour ' +
                'la machine possédée : temps contractuel sur jours ouvrés, ' +
                'après réduction des congés offerts et des fermetures ' +
                'd’entreprise. Week-ends et jours fériés sont déjà hors des ' +
                'jours ouvrés.',
            ],
            [
              'tO — atelier ouvert',
              'TO — temps ouvert',
              'Le temps disponible pour l’entreprise après retrait des ' +
                'vacances payées : la présence rémunérée, donc due à ' +
                'l’entreprise, avant tout événement et avant toute ' +
                'affectation du temps.',
            ],
            [
              'tR — temps requis',
              'TR — capacité pratique',
              'Du temps ouvert, on soustrait les congés spéciaux (absences ' +
                'planifiées et indemnisées : maternité, service) et le ' +
                'temps de gestion : administration, direction, supervision ' +
                'de production. Le résultat est le temps productif ' +
                'normalisé, la « maintenance préventive » d’une ' +
                'organisation humaine. Une divergence assumée : voir la note.',
            ],
            [
              'tF — temps de fonctionnement',
              'Heures travaillées sur les mandats',
              'Facturables et non facturables confondues. Attention : dans ' +
                'Palestre, le sigle TF désigne le seul temps FACTURABLE ' +
                '(Coût heure TF), un sous-ensemble du tF normatif.',
            ],
            [
              'tN — temps net',
              'Temps standard par prestation',
              'Pas de cadence machine sur une prestation intellectuelle ; ' +
                'le temps de référence existe pourtant : c’est le temps ' +
                'standard des équations de temps du TDABC, posé en ' +
                'hypothèse, jamais mesuré.',
            ],
            [
              'tU — temps utile',
              'Heures retenues au client',
              'La non-qualité du service est le write-off : l’écart entre ' +
                'heures passées et heures facturées.',
            ],
          ],
          note:
            'Une seule divergence de fond, et elle est délibérée : la norme ' +
            'sort l’absence de charge du temps requis, ce qui fait remonter ' +
            'le TRS quand le carnet se vide. Kaplan (et Palestre) la ' +
            'gardent dans la capacité pratique, pour que le coût horaire ne ' +
            'se gonfle pas quand l’activité baisse et que la capacité ' +
            'inemployée devienne un montant visible. La norme mesure une ' +
            'performance ; une méthode de coût évite un artefact de ' +
            'tarification.',
        },
      },
      {
        kind: 'sources',
        label: 'Sources',
        items: [
          'Kaplan R. S. & Anderson S. R., Time-Driven Activity-Based Costing (Harvard Business Review Press, 2007).',
          'Le référentiel industriel : fiche « Taux de rendement en industrie (TRS, TRG, TRE) ».',
          'Le moteur de coûts : fiche « Méthode Time-Driven ABC (TDABC) ».',
        ],
      },
    ],
  },

  {
    id: 'cascade-temps-rendement',
    icon: 'gauge',
    title: 'Taux de rendement en industrie (TRS, TRG, TRE)',
    category: 'Méthode',
    summary:
      'Ce qui est retranché à chaque étage, du temps calendaire au temps ' +
      'utile, et les trois taux qui en découlent : le référentiel ' +
      'industriel dont s’inspire la cascade des temps de Palestre.',
    updated: '2026-08-22',
    pages: ['time-standard', 'chr-pcr'],
    sections: [
      {
        kind: 'text',
        label: 'Source normative',
        paragraphs: [
          'NF E 60-182 (AFNOR) — « Moyens de production. Indicateurs de ' +
            'performances. Taux de rendement synthétique (TRS), taux de ' +
            'rendement global (TRG), taux de rendement économique (TRE) ». ' +
            'Elle définit les temps d’état d’un moyen de production et les ' +
            'éléments nécessaires à l’évaluation de ces trois indicateurs.',
          'Équivalent international : ISO 22400-2, « Key performance ' +
            'indicators for manufacturing operations management », qui ' +
            'formalise l’OEE (Overall Equipment Effectiveness), l’appellation ' +
            'anglo-saxonne du TRS.',
          'En Suisse, il n’existe pas de norme SNV propre au sujet : la ' +
            'pratique se réfère à la NF E 60-182 ou à l’ISO 22400-2 selon le ' +
            'contexte du donneur d’ordre.',
        ],
      },
      {
        kind: 'illustration',
        label: 'La cascade des temps',
        paragraphs: [
          'Chaque étage retranche une catégorie de perte au précédent. Le ' +
            'nom du temps ne dit rien : ce qui compte est ce qui vient d’en ' +
            'être retiré.',
          'Le tT à 8’760 h est un maximum théorique, pas une donnée ' +
            'd’exploitation : il reste identique que l’atelier tourne en 1×8, ' +
            '2×8 ou 3×8. C’est le temps d’ouverture (tO) qui porte ce choix ' +
            'd’organisation, et l’écart TRG → TRE qui le mesure.',
        ],
        table: {
          head: ['Temps', 'Définition', 'Retranché en amont'],
          rows: [
            [
              'tT — Temps total',
              'Temps calendaire intégrant tous les états possibles du moyen : ' +
                '24 h/jour, 168 h/semaine, 8’760 h/an',
              '—',
            ],
            [
              'tO — Temps d’ouverture',
              'Plage pendant laquelle l’atelier est ouvert',
              'Fermetures planifiées : week-ends, jours fériés, congés',
            ],
            [
              'tR — Temps requis',
              'Partie du temps d’ouverture pendant laquelle l’utilisateur ' +
                'engage son moyen avec la volonté de produire',
              'Arrêts planifiés : maintenance préventive, pauses, réunions, ' +
                'nettoyage, et absence de charge',
            ],
            [
              'tF — Temps de fonctionnement',
              'Partie du temps requis pendant laquelle le moyen produit des ' +
                'pièces, bonnes ou mauvaises, dans le respect ou non du temps ' +
                'de cycle de référence',
              'Arrêts propres (pannes, réglages, contrôles) et arrêts induits ' +
                '(manque matière, causes externes)',
            ],
            [
              'tN — Temps net',
              'Partie du temps de fonctionnement pendant laquelle le moyen ' +
                'aurait produit à la cadence de référence. Non mesurable : ' +
                'obtenu par calcul',
              'Écarts de cadence : micro-arrêts, ralentissements',
            ],
            [
              'tU — Temps utile',
              'Temps théorique correspondant aux seules pièces BONNES ' +
                'produites à la cadence de référence',
              'Non-qualité : rebuts, retouches',
            ],
          ],
          note:
            'La norme distingue les arrêts PROPRES (pannes, réglages), ' +
            'imputables au moyen, des arrêts INDUITS (manque matière, causes ' +
            'externes). Calculer la disponibilité sur les seuls arrêts propres ' +
            'isole ce qui relève de l’atelier de ce qui relève de ' +
            'l’approvisionnement : sans cette séparation, un problème ' +
            'fournisseur se lit comme une contre-performance de production.',
        },
      },
      {
        kind: 'illustration',
        label: 'Les trois ratios intermédiaires',
        table: {
          head: ['Ratio', 'Formule', 'Ce qu’il mesure'],
          rows: [
            [
              'Disponibilité opérationnelle',
              'tF / tR',
              'La part du temps engagé où le moyen a effectivement tourné',
            ],
            [
              'Performance',
              'tN / tF',
              'L’écart à la cadence de référence : micro-arrêts, ralentissements',
            ],
            [
              'Qualité',
              'tU / tN',
              'La part produite bonne, ou pièces bonnes ÷ pièces réalisées',
            ],
          ],
          note:
            'tN n’est pas mesuré, il est calculé. La Performance est donc un ' +
            'résidu : elle absorbe toute erreur de mesure commise en amont sur ' +
            'tF. Un taux de performance médiocre commence par interroger la ' +
            'qualité du relevé des temps d’arrêt, avant la cadence machine.',
        },
      },
      {
        kind: 'illustration',
        label: 'Les trois taux de sortie',
        paragraphs: [
          'Trois taux, un même numérateur (tU) et trois dénominateurs. ' +
            'Le choix du dénominateur est le choix de la question posée.',
        ],
        table: {
          head: ['Taux', 'Formule', 'Question à laquelle il répond'],
          rows: [
            [
              'TRS',
              'tU / tR = Disponibilité × Performance × Qualité',
              'Quand j’engage mon moyen, tourne-t-il bien ? Performance de ' +
                'l’exploitation, à périmètre engagé',
            ],
            [
              'TRG',
              'tU / tO',
              'Ai-je de quoi l’engager ? Il intègre l’absence de charge et la ' +
                'maintenance planifiée : vision ordonnancement et charge ' +
                'commerciale',
            ],
            [
              'TRE',
              'tU / tT',
              'Mon actif immobilisé rapporte-t-il ? Vision retour sur ' +
                'investissement',
            ],
          ],
          note:
            'L’écart TRS → TRG isole les pertes de pilotage (planification, ' +
            'sous-charge). L’écart TRG → TRE isole le choix d’organisation du ' +
            'travail (1×8 contre 3×8) : c’est un arbitrage d’investissement, ' +
            'pas un problème d’atelier. Cette lecture en trois niveaux est ce ' +
            'qui rend la norme exploitable en analyse de rentabilité, et pas ' +
            'seulement en amélioration continue.',
        },
      },
      {
        kind: 'text',
        label: 'Ce que la norme ne dit pas',
        paragraphs: [
          'Le TRE est souvent présenté comme « le seul indicateur qui parle au ' +
            'directeur financier ». C’est vrai d’un atelier capitalistique, où ' +
            'une machine à 800’000 CHF dort la nuit : le dénominateur 8’760 h ' +
            'a un sens économique, il mesure un capital immobilisé.',
          'Dans une PME de services, l’actif est humain. Personne ne vend ' +
            '8’760 heures : le TRE y produit un ratio de 20 % qui ne se ' +
            'compare à rien et n’ouvre aucune décision. Le bon niveau de ' +
            'lecture y est le TRG, la charge commerciale rapportée au temps ' +
            'd’ouverture.',
          'Le repère « TRS de classe mondiale à 85 % » (90 % × 95 % × 99 %) ' +
            'circule largement mais ne figure dans aucune des deux normes. ' +
            'C’est un usage de l’industrie automobile, pas une exigence ' +
            'normative : il ne se transpose ni à la petite série ni aux ' +
            'services.',
        ],
      },
      {
        kind: 'sources',
        label: 'Sources',
        items: [
          'NF E 60-182 (AFNOR) — Moyens de production : indicateurs de performances (TRS, TRG, TRE).',
          'ISO 22400-2 — Key performance indicators for manufacturing operations management (définitions de l’OEE).',
          'Calculateur TRS et fiches pédagogiques : https://www.pilote-expertise.fr/outils/calculateur-trs',
          'Pour la transposition au service : fiche « Cascade des temps Palestre ».',
        ],
      },
    ],
  },

  {
    id: 'tdabc',
    icon: 'calculator',
    title: 'Méthode Time-Driven ABC (TDABC)',
    category: 'Méthode',
    summary:
      'Calcul de coût piloté par le temps : un seul inducteur (le temps) et un ' +
      'dénominateur stable (la capacité pratique), pour un coût horaire ' +
      'maintenable. C’est le moteur de coûts de Palestre.',
    updated: '2026-10-20',
    pages: ['cost-rates-hub', 'chr-pcr', 'tdabc-rentabilite-client'],
    sections: [
      {
        kind: 'text',
        label: 'Explication',
        paragraphs: [
          'L’ABC classique (Activity-Based Costing) répartit les charges ' +
            'sur des activités via une foule d’inducteurs, qu’il faut ' +
            'réenquêter à chaque période : précis mais lourd à maintenir.',
          'Le Time-Driven ABC (Kaplan & Anderson, 2004) simplifie : un seul ' +
            'inducteur, le temps. Deux paramètres suffisent : (a) le coût ' +
            'd’une unité de temps d’une ressource, soit le taux de revient ' +
            'de capacité (CCR), et (b) le temps consommé par chaque prestation ' +
            '(via des équations de temps).',
          'Clé de la méthode : le dénominateur est la capacité PRATIQUE (heures ' +
            'réellement mobilisables, ≈ 80–85 % du théorique), pas les heures ' +
            'effectivement vendues. Ce dénominateur stable (méthode Kaplan) ' +
            'évite de regonfler le taux quand l’activité baisse, et révèle ' +
            'au contraire le coût de la capacité inutilisée (idle capacity).',
        ],
      },
      {
        kind: 'illustration',
        label: 'Illustration',
        paragraphs: [
          'Un groupe ressources à 200’000 CHF de charges annuelles, pour une ' +
            'capacité pratique de 10’000 heures :',
        ],
        table: {
          head: ['Élément', 'Valeur'],
          rows: [
            ['Coût total du groupe ressources', '200’000 CHF / an'],
            ['Capacité pratique', '10’000 h / an'],
            ['CCR = coût ÷ capacité', '20 CHF / h'],
            ['Coût d’une prestation de 5 h', '100 CHF'],
          ],
          note:
            'Si seules 8’000 h sont réellement vendues, les 2’000 h ' +
            'restantes × 20 CHF = 40’000 CHF de capacité inutilisée, ' +
            'rendue visible au lieu d’être masquée dans un taux gonflé.',
        },
      },
      {
        kind: 'palestre',
        label: 'Dans Palestre',
        paragraphs: [
          'Le module Analytics calcule le coût d’une heure de capacité de ' +
            'chaque groupe ressources dans la section « Coût heure disp. ' +
            '(CCR) ». Il part des coûts du personnel (People) et des charges ' +
            'indirectes, puis les divise par la capacité pratique saisie ' +
            'dans « Saisie des heures ».',
          'La section « Coût heure TF (PCR) » en déduit le coût d’une heure ' +
            'facturable, et « Rentabilité service » en tire la marge de ' +
            'chaque service. L’utilisation de la capacité se lit dans ' +
            '« Saisie des heures », onglet « Vue d’ensemble ». Règle du ' +
            'moteur : coût horaire = coût total ÷ capacité pratique.',
        ],
        link: { pageKey: 'cost-rates-hub', label: 'Voir Coût heure disp. (CCR)' },
      },
      {
        kind: 'sources',
        label: 'Sources',
        items: [
          'Kaplan R. S. & Anderson S. R., Time-Driven Activity-Based Costing (Harvard Business Review Press, 2007).',
          'Synthèse méthodologique et comparaison ABC / TDABC / UVA : docs/savoirs/uva-methode.md.',
        ],
      },
    ],
  },

  {
    id: 'delais-rotation-bfr',
    icon: 'refresh',
    title: 'Délais et rotations du BFR (DSO, DIO, DPO)',
    category: 'Finance',
    summary:
      'Trois délais en jours qui mesurent la vitesse du cycle d’exploitation : ' +
      'encaissement clients (DSO), écoulement des stocks (DIO), paiement ' +
      'fournisseurs (DPO). Leur inverse est la rotation (nombre de tours par an).',
    updated: '2026-06-24',
    pages: ['financials-indicators', 'forecast-wc'],
    sections: [
      {
        kind: 'text',
        label: 'Explication',
        paragraphs: [
          'Le délai et la rotation décrivent la même réalité sous deux angles. ' +
            'Le délai se lit en jours : combien de temps un franc reste immobilisé ' +
            'dans les créances, les stocks ou les dettes. La rotation se lit en ' +
            'nombre de tours par an : combien de fois le poste « se renouvelle ». ' +
            'Les deux sont liés : rotation = 365 ÷ délai.',
          'DSO (délai de recouvrement clients) : jours entre la facture et ' +
            'l’encaissement. DIO (délai d’écoulement des stocks) : jours de ' +
            'stock avant vente. DPO (délai de paiement fournisseurs) : jours ' +
            'avant de régler les achats. Le besoin en fonds de roulement ' +
            'd’exploitation suit, en jours de chiffre d’affaires : BFRE ≈ ' +
            'DSO + DIO − DPO. Allonger le DPO ou raccourcir le DSO/DIO libère ' +
            'de la trésorerie.',
          'Soldes moyens : un délai met en rapport un stock (créances, stocks, ' +
            'dettes) avec un flux sur 12 mois. Pour qu’ils soient au même pas, ' +
            'on prend le solde MOYEN de la période (moyenne des 12 derniers ' +
            'mois), pas le solde ponctuel de fin de mois : sinon un pic ou un ' +
            'creux de clôture fausserait le délai.',
          'Subtilité TVA : les créances et dettes au bilan sont TTC, alors que ' +
            'le chiffre d’affaires et les achats au compte de résultat sont HT. ' +
            'Palestre majore donc le dénominateur de la TVA (× 1 + taux) pour le ' +
            'DSO et le DPO. Les stocks et leur coût étant tous deux HT, le DIO ' +
            'n’a pas de correction. Les exemples ci-dessous sont simplifiés ' +
            '(hors TVA, solde unique) pour la lisibilité.',
        ],
      },
      {
        kind: 'illustration',
        label: 'Trois exemples simples',
        paragraphs: [
          'Chaque ligne illustre un poste avec un cas volontairement rond :',
        ],
        table: {
          head: ['Indicateur', 'Exemple', 'Délai', 'Rotation'],
          rows: [
            ['DSO — clients', 'CA 1’200 K / an (≈ 100 K / mois), créances-clients 100 K en fin d’année', '≈ 30 jours (1 mois)', '12× / an'],
            ['DIO — stocks', 'Stock équivalent à 6 mois d’écoulement (coût des marchandises vendues)', '≈ 180 jours (6 mois)', '2× / an'],
            ['DPO — fournisseurs', 'Achats réglés à 60 jours', '60 jours', '≈ 6× / an'],
          ],
          note:
            'DSO : 100 K ÷ 1’200 K × 365 ≈ 30 j ; le client paie en ~1 mois, ' +
            'donc les créances tournent 12 fois dans l’année. DIO : 6 mois de ' +
            'stock = 2 tours par an. DPO : payer à 60 jours = 365 ÷ 60 ≈ 6 ' +
            'tours par an. Cycle net (BFRE) de cet exemple : 30 + 180 − 60 = ' +
            '150 jours de CA immobilisés.',
        },
      },
      {
        kind: 'palestre',
        label: 'Dans Palestre',
        paragraphs: [
          'Les trois délais et leurs rotations se lisent dans la section BFRE ' +
            '(Finance), en base 12 mois glissants. Les flux du calcul (CA net, ' +
            'achats, coût des marchandises) s’affichent en tuiles au-dessus de ' +
            'la card des délais ; l’icône « i » rappelle la formule. Les comptes ' +
            'utilisés (créances-clients, stocks, dettes fournisseurs) sont ceux ' +
            'figés dans la structure standard du bilan.',
        ],
        link: { pageKey: 'financials-bfre', label: 'Voir la section BFRE' },
      },
      {
        kind: 'sources',
        label: 'Sources',
        items: [
          'Définitions usuelles du fonds de roulement et du cycle d’exploitation (working capital cycle).',
          'Convention Palestre : base 12 mois roulants, créances/dettes TTC corrigées de la TVA pour le DSO et le DPO.',
        ],
      },
    ],
  },

  {
    id: 'surendettement-fonds-propres',
    icon: 'scale',
    title: 'Surendettement et érosion des fonds propres',
    category: 'Juridique',
    summary:
      "L'échelle de protection du droit suisse des sociétés quand les fonds " +
      'propres se dégradent : alerte solvabilité → perte de capital → ' +
      'surendettement et avis au tribunal.',
    updated: '2026-06-18',
    pages: ['financials-bs', 'financials-cash-plan'],
    sections: [
      {
        kind: 'law',
        label: 'Extrait de loi',
        articles: [
          {
            ref: 'Art. 725 CO — Menace d’insolvabilité',
            text:
              "Le conseil d'administration surveille la solvabilité de la " +
              "société. S'il y a des raisons sérieuses d'admettre que la " +
              'société pourrait devenir insolvable, le conseil ' +
              'd’administration prend des mesures visant à garantir sa ' +
              'solvabilité. Au besoin, il prend des mesures ' +
              'd’assainissement supplémentaires ou en propose au tribunal.',
          },
          {
            ref: 'Art. 725a CO — Perte de capital',
            text:
              'Lorsqu’il ressort du dernier compte annuel que les actifs, ' +
              'après déduction des dettes, ne couvrent plus la moitié de la ' +
              'somme du capital-actions et des réserves légales non ' +
              'remboursables aux actionnaires, le conseil d’administration ' +
              'prend des mesures propres à mettre fin à la perte de capital.',
          },
          {
            ref: 'Art. 725b CO — Surendettement',
            text:
              'S’il existe des raisons sérieuses d’admettre que les ' +
              'dettes de la société ne sont plus couvertes par les actifs, le ' +
              'conseil d’administration établit immédiatement des comptes ' +
              'intermédiaires à la valeur d’exploitation et à la valeur de ' +
              'liquidation. Si les deux montrent un surendettement, le conseil ' +
              'en avise le tribunal, à moins que des créanciers acceptent une ' +
              'postposition de leurs créances à hauteur suffisante.',
          },
        ],
        source:
          'Code des obligations (CO), art. 725 à 725b — révision du droit de ' +
          'la SA en vigueur depuis le 1ᵉʳ janvier 2023.',
      },
      {
        kind: 'text',
        label: 'Explication',
        paragraphs: [
          'Le droit suisse traite la dégradation des fonds propres comme un ' +
            'escalier à trois marches, du plus léger au plus grave. À chaque ' +
            'marche, c’est au conseil d’administration d’agir : ' +
            "l'inaction engage sa responsabilité personnelle.",
          '1) Menace d’insolvabilité (art. 725) : la société risque de ne ' +
            'plus pouvoir payer ses factures à l’échéance. C’est un ' +
            'critère de liquidité (trésorerie), pas de bilan.',
          '2) Perte de capital (art. 725a) : les fonds propres comptables sont ' +
            'tombés sous la moitié du capital-actions augmenté des réserves ' +
            'légales protégées. Signal d’alerte : il faut des mesures, ' +
            'mais la société n’est pas encore en danger immédiat.',
          '3) Surendettement (art. 725b) : les dettes dépassent les actifs, ' +
            'les fonds propres sont négatifs. Le conseil doit dresser des ' +
            'comptes intermédiaires et, sauf postposition de créances, ' +
            'aviser le tribunal (l’ancien « dépôt de bilan »).',
          'La « sous-capitalisation » au sens économique précède cet escalier ' +
            ': des fonds propres trop minces dès le départ pour absorber des ' +
            'pertes ou financer la croissance. Ce n’est pas un seuil légal, ' +
            'mais c’est elle qui fait franchir les marches plus vite.',
        ],
      },
      {
        kind: 'illustration',
        label: 'Illustration',
        paragraphs: [
          'Une SA avec un capital-actions de 100’000 CHF et 20’000 CHF ' +
            'de réserves légales protégées. Le seuil de perte de capital vaut ' +
            'la moitié de (100’000 + 20’000) = 60’000 CHF de fonds ' +
            'propres. On suit les fonds propres (actifs − dettes) qui se dégradent :',
        ],
        table: {
          head: ['Fonds propres (actifs − dettes)', 'Marche', 'Obligation du conseil'],
          rows: [
            ['90’000 CHF', 'Situation saine', 'Surveillance courante'],
            ['55’000 CHF', 'Perte de capital (725a)', 'Mesures d’assainissement'],
            ['10’000 CHF', 'Proche du surendettement', 'Comptes intermédiaires à surveiller'],
            ['− 15’000 CHF', 'Surendettement (725b)', 'Avis au tribunal, sauf postposition'],
          ],
          note:
            'La trésorerie peut imposer d’agir au titre de l’art. 725 ' +
            '(insolvabilité) bien avant que le bilan ne franchisse ces seuils.',
        },
      },
      {
        kind: 'palestre',
        label: 'Dans Palestre',
        paragraphs: [
          'Le franchissement de ces seuils se lit sur le Bilan (fonds propres) ' +
            'et l’approche d’une rupture de trésorerie se voit dans le ' +
            'Cash forecast (autonomie / runway, mois en découvert).',
        ],
        link: { pageKey: 'financials-bs', label: 'Voir le Bilan' },
      },
      {
        kind: 'sources',
        label: 'Sources',
        items: [
          'Code des obligations (CO), art. 725 à 725b (révision du droit de la SA, 2023).',
          'Le texte légal complet fait foi ; cette fiche en est une lecture ' +
            'simplifiée et ne constitue pas un avis juridique.',
        ],
      },
    ],
  },
  {
    id: 'effectif-ept-contrats-mouvements',
    icon: 'users',
    title: 'Effectif en EPT : contrats et mouvements',
    category: 'Méthode',
    summary:
      'L’effectif se compte en équivalents plein temps, mois par mois. Les ' +
      'mois clos lisent les contrats signés ; les mois ouverts y ajoutent ' +
      'les mouvements simulés. Un mouvement ne réécrit jamais le passé.',
    updated: '2026-09-16',
    pages: ['hr-headcount', 'hr-planning', 'hr-trajectory', 'hr-home'],
    sections: [
      {
        kind: 'text',
        label: 'Ce que compte un EPT',
        paragraphs: [
          'Un EPT (équivalent plein temps) vaut un temps plein du calendrier ' +
            'de l’entreprise : deux mi-temps font un EPT. Le taux d’activité ' +
            'du contrat le fractionne, et un contrat qui commence ou finit en ' +
            'cours de mois compte pour le mois entier : on lit un effectif, ' +
            'pas une paie.',
          'Le trajet d’un exercice se lit entre deux bornes, l’EPT au ' +
            '1er janvier et l’EPT de décembre ; la moyenne de l’exercice ' +
            'dit ce qui a réellement été porté sur l’année.',
        ],
      },
      {
        kind: 'illustration',
        label: 'Deux sources, une seule frise',
        table: {
          head: ['Mois', 'Source lue', 'Ce qui peut encore bouger'],
          rows: [
            ['Clos (jusqu’au dernier réel)', 'Contrats signés', 'Rien : un mouvement n’écrit jamais un mois clos'],
            ['Ouvert, vue Contrats', 'Contrats signés', 'Un nouveau contrat, un avenant'],
            ['Ouvert, vue Simulation', 'Contrats + mouvements', 'Une arrivée, un départ, un changement de taux posés en mouvement'],
          ],
          note: 'Les deux vues coïncident sur tout mois clos : c’est le test qui dit que la simulation n’a pas touché le réel.',
        },
      },
      {
        kind: 'text',
        label: 'Le mouvement, une intention datée',
        paragraphs: [
          'Un mouvement simule ce qui n’est pas encore signé : un ' +
            'engagement, un départ, un changement de taux, à une date et ' +
            'pour un taux. Il vit sur les mois ouverts et se lit en EPT ' +
            'signé (+0.8, −1.0) ou en francs par mois sur la masse ' +
            'salariale. Le jour où le contrat est signé, le mouvement est ' +
            'promu en contrat : la frise ne change pas, la source change.',
        ],
      },
      {
        kind: 'palestre',
        label: 'Dans Palestre',
        paragraphs: [
          'People › Effectifs porte la frise et ses deux vues ; l’onglet ' +
            'Mouvements range ce qui est posé ; le Prévisionnel et la ' +
            'Trajectoire projettent la même donnée par le même moteur, si ' +
            'bien que deux écrans ne peuvent pas répondre deux chiffres ' +
            'différents.',
        ],
        link: { pageKey: 'hr-headcount', label: 'Voir Effectifs' },
      },
    ],
  },
];

export default SAVOIRS;
